/**
 * A карась in the water: three boxes and a tail that flicks.
 *
 * Much simpler than the livestock, and deliberately so - a fish is a hand's
 * length of thing seen through moving water, and every pixel spent on detail
 * here is a pixel nobody will ever resolve.
 *
 * Unlit, like everything else in this scene. There is not one light source in
 * it; see scripts/check-lighting.mjs, which exists because the animals were
 * shipped rendering black once already.
 */

import * as THREE from "three";
import { KARAS_PARTS, KARAS_COLOURS } from "../core/entity/fishSkin.js";

/** Blocks per part pixel. A карась is about a third of a block long. */
const PIXEL = 0.36 / KARAS_PARTS.body.d;

/**
 * The three boxes and three materials every карась shares.
 *
 * Built once. Ninety fish each building their own meant two hundred and
 * seventy geometries and two hundred and seventy materials for three distinct
 * shapes and three colours - all of it GPU memory, none of it different.
 */
interface KarasGpu {
  readonly body: THREE.BufferGeometry;
  readonly dorsal: THREE.BufferGeometry;
  readonly tail: THREE.BufferGeometry;
  readonly bodyMat: THREE.MeshBasicMaterial;
  readonly backMat: THREE.MeshBasicMaterial;
  readonly finMat: THREE.MeshBasicMaterial;
  /** What each material is before the light of the day is multiplied into it. */
  readonly bases: readonly [THREE.Color, THREE.Color, THREE.Color];
}
let shared: KarasGpu | null = null;

function karasGpu(): KarasGpu {
  if (shared !== null) return shared;

  const material = (colour: number): THREE.MeshBasicMaterial => new THREE.MeshBasicMaterial({
    color: colour,
    // Fins are one pixel thick and are seen edge-on half the time.
    side: THREE.DoubleSide,
  });
  const box = (size: { w: number; h: number; d: number }): THREE.BufferGeometry =>
    new THREE.BoxGeometry(size.w * PIXEL, size.h * PIXEL, size.d * PIXEL);

  shared = {
    body: box(KARAS_PARTS.body),
    dorsal: box(KARAS_PARTS.fin),
    tail: box(KARAS_PARTS.tail),
    bodyMat: material(KARAS_COLOURS.body),
    backMat: material(KARAS_COLOURS.back),
    finMat: material(KARAS_COLOURS.fin),
    bases: [
      new THREE.Color(KARAS_COLOURS.body),
      new THREE.Color(KARAS_COLOURS.back),
      new THREE.Color(KARAS_COLOURS.fin),
    ],
  };
  return shared;
}

/**
 * Every карась darkens together, because every карась is the same fish.
 *
 * Called once a frame rather than once per fish: the materials are shared, so
 * setting the tint ninety times set the same three colours ninety times.
 */
export function tintKaras(color: THREE.Color): void {
  const gpu = shared;
  if (gpu === null) return;
  gpu.bodyMat.color.copy(gpu.bases[0]).multiply(color);
  gpu.backMat.color.copy(gpu.bases[1]).multiply(color);
  gpu.finMat.color.copy(gpu.bases[2]).multiply(color);
}

export class FishModel {
  readonly group = new THREE.Group();
  private readonly tail: THREE.Group;

  constructor() {
    const gpu = karasGpu();
    const b = KARAS_PARTS.body;

    this.group.add(new THREE.Mesh(gpu.body, gpu.bodyMat));

    const dorsal = new THREE.Mesh(gpu.dorsal, gpu.backMat);
    dorsal.position.set(
      0, (b.h * PIXEL) / 2 + (KARAS_PARTS.fin.h * PIXEL) / 2, 0,
    );
    this.group.add(dorsal);

    // The tail hangs off a pivot at the back of the body, so it sweeps rather
    // than sliding about - which is the only animation a fish needs.
    this.tail = new THREE.Group();
    this.tail.position.set(0, 0, (b.d * PIXEL) / 2);
    const fin = new THREE.Mesh(gpu.tail, gpu.finMat);
    fin.position.z = (KARAS_PARTS.tail.d * PIXEL) / 2;
    this.tail.add(fin);
    this.group.add(this.tail);
  }

  /** Point it, and sweep the tail. */
  setPose(yaw: number, pitch: number, phase: number, swimming: number): void {
    this.group.rotation.set(0, 0, 0);
    this.group.rotation.y = yaw;
    this.group.rotation.x = -pitch;
    // Always a little movement, even hanging still: a fish holds station by
    // sculling, and one frozen solid in the water reads as a dead one.
    this.tail.rotation.y = Math.sin(phase * 3) * (0.25 + 0.45 * swimming);
  }

  /**
   * Nothing to give back, and nothing to tint one at a time.
   *
   * Both the geometry and the materials belong to the species. The tint goes
   * through tintKaras once a frame instead of ninety times.
   */
  dispose(): void {
    // Intentionally empty: see karasGpu.
  }
}
